import React, { useEffect, useState } from "react";
import { Search, MoreVertical } from "lucide-react";

import {
    getAllClientClaims,
    deleteClientClaim
} from "../../api/ClientClaimApi";


import AddClientClaimModal from "./AddClientClaimModal";

import EditClientClaimModal from "./EditClientClaimModal";

import ViewClientClaimModal from "./ViewClientClaimModal";

import { toast } from "../shared/Toast";

import { showConfirm } from "../shared/ConfirmContext";

const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    } catch (e) {
        return dateStr;
    }
};

const StatCard = ({
    value,
    label,
    active,
    onClick
}) => (

    <div
        onClick={onClick}
        style={{
            background: active ? "#E8F5EE" : "#fff",
            flex: 1,
            borderRadius: 8,
            padding: 20,
            cursor: "pointer",
            border: active
                ? "2px solid #20B967"
                : "2px solid transparent",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
        }}
    >

        <div
            style={{
                fontSize: 42,
                fontWeight: 700,
                color: "#20B967"
            }}
        >
            {value}
        </div>

        <div
            style={{
                marginTop: 10,
                fontWeight: 600
            }}
        >
            {label}
        </div>

    </div>

);

const TableHeader = () => (

    <div
        style={{
            display: "flex",
            background: "#212A30",
            color: "#fff",
            padding: 12,
            fontWeight: 600,
            fontSize: 13,
            gap: 16
        }}
    >

        <div style={{ flex: 2 }}>
            Client
        </div>

        <div style={{ flex: 1 }}>
            From
        </div>

        <div style={{ flex: 1 }}>
            To
        </div>

        <div style={{ flex: 1 }}>
            Trips
        </div>

        <div style={{ flex: 1 }}>
            Amount
        </div>

        <div style={{ flex: 1 }}>
            Deduction
        </div>

        <div style={{ flex: 1 }}>
            Final Claim
        </div>

        <div style={{ flex: 1 }}>
            Status
        </div>

        <div style={{ width: 80 }}>
            Action
        </div>

    </div>

);

const ActionsMenu = ({
    claim,
    onDelete,
    onView,
    onEdit
}) => {

    const [open, setOpen] = useState(false);

    return (

        <div style={{ position: "relative" }}>

            <MoreVertical
                size={20}
                style={{ cursor: "pointer", color: "#374151" }}
                onClick={() => setOpen(!open)}
            />

            {

                open && (

                    <div
                        style={{
                            position: "absolute",
                            right: 0,
                            top: 25,
                            background: "#fff",
                            borderRadius: 8,
                            overflow: "hidden",
                            boxShadow:
                                "0 4px 15px rgba(0,0,0,.15)",
                            zIndex: 10
                        }}
                    >

                        <div
                            onClick={() => onView(claim.id)}
                            style={{
                                padding: 12,
                                cursor: "pointer"
                            }}
                        >
                            View
                        </div>

                        <div
                            onClick={() => onEdit(claim.id)}
                            style={{
                                padding: 12,
                                cursor: "pointer"
                            }}
                        >
                            Edit
                        </div>

                        <div
                            onClick={() => onDelete(claim.id)}
                            style={{
                                padding: 12,
                                cursor: "pointer",
                                color: "red"
                            }}
                        >
                            Delete
                        </div>

                    </div>

                )

            }

        </div>

    );

};

const TableRow = ({
    claim,
    onDelete,
    onView,
    onEdit
}) => (

    <div
        style={{
            display: "flex",
            padding: 15,
            borderBottom: "1px solid #eee",
            alignItems: "center",
            gap: 16
        }}
    >

        <div style={{ flex: 2, wordBreak: 'break-word' }}>
            {claim.client?.clientName}
        </div>

        <div style={{ flex: 1 }}>
            {formatDate(claim.fromDate)}
        </div>

        <div style={{ flex: 1 }}>
            {formatDate(claim.toDate)}
        </div>

        <div style={{ flex: 1 }}>
            {claim.totalTrips}
        </div>

        <div style={{ flex: 1 }}>
            ₹ {claim.totalAmount}
        </div>

        <div style={{ flex: 1 }}>
            ₹ {claim.deduction}
        </div>

        <div style={{ flex: 1 }}>
            ₹ {claim.finalClaimAmount}
        </div>

        <div style={{ flex: 1 }}>
            {claim.status}
        </div>

        <div style={{ width: 80 }}>


            <ActionsMenu

                claim={claim}

                onDelete={onDelete}

                onView={onView}

                onEdit={onEdit}

            />

        </div>

    </div>

);

const ClientClaim = () => {

    const [claims, setClaims] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");

    const [filter, setFilter] = useState("all");

    const [showViewModal, setShowViewModal] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);

    const [selectedClaimId, setSelectedClaimId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const fetchClientClaims = async () => {

        try {

            setLoading(true);

            const response = await getAllClientClaims();

            setClaims(
                response.claims || response.data || []
            );

        }
        catch (error) {

            console.log(error);

        }
        finally {

            setLoading(false);

        }

    };

    useEffect(() => {

        fetchClientClaims();

    }, []);

    const total = claims.length;

    const draft = claims.filter(
        x => x.status === "Draft"
    ).length;

    const approved = claims.filter(
        x => x.status === "Approved"
    ).length;

    const paid = claims.filter(
        x => x.status === "Paid"
    ).length;

    const filtered = claims.filter((claim) => {

        const matchSearch = (
            claim.client?.clientName || ""
        )
            .toLowerCase()
            .includes(search.toLowerCase());

        const matchStatus =

            filter === "all"

                ? true

                : claim.status === filter;

        return matchSearch && matchStatus;

    });

    const handleDelete = async (id) => {
        const confirmDelete = await showConfirm({
            title: "Delete Client Claim",
            message: "Are you sure you want to delete this Client Claim?",
            danger: true,
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
        });

        if (!confirmDelete) return;

        try {
            await deleteClientClaim(id);
            toast.success("Client Claim deleted successfully.");
            fetchClientClaims();
        }
        catch (error) {
            console.log(error);
            toast.error(error.response?.data?.message || "Unable to delete Client Claim");
        }
    };
    const handleView = (id) => {

        setSelectedClaimId(id);

        setShowViewModal(true);

    };


    const handleEdit = (id) => {

        setSelectedClaimId(id);

        setShowEditModal(true);

    };

    return (

        <div>

            {/* Statistics */}

            <div
                style={{
                    display: "flex",
                    gap: 20,
                    marginBottom: 20
                }}
            >

                <StatCard
                    value={total}
                    label="Total Claims"
                    active={filter === "all"}
                    onClick={() => setFilter("all")}
                />

                <StatCard
                    value={draft}
                    label="Draft"
                    active={filter === "Draft"}
                    onClick={() => setFilter("Draft")}
                />

                <StatCard
                    value={approved}
                    label="Approved"
                    active={filter === "Approved"}
                    onClick={() => setFilter("Approved")}
                />

                <StatCard
                    value={paid}
                    label="Paid"
                    active={filter === "Paid"}
                    onClick={() => setFilter("Paid")}
                />

            </div>

            {/* Search */}

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20
                }}
            >

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        width: 350,
                        background: "#F5F1FA",
                        borderRadius: 20,
                        padding: "10px 15px"
                    }}
                >

                    <input
                        type="text"
                        placeholder="Search Client"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            background: "transparent"
                        }}
                    />

                    <Search size={18} />

                </div>

                <button
                    onClick={() => setShowAddModal(true)}
                    style={{
                        background: "#20B967",
                        color: "#fff",
                        border: "none",
                        padding: "12px 20px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontWeight: 600
                    }}
                >
                    + Add Client Claim
                </button>

            </div>

            {/* Table */}

            <div
                style={{
                    background: "#fff",
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    boxShadow: "0 2px 10px rgba(0,0,0,.05)"
                }}
            >

                <TableHeader />

                {

                    loading ?

                        <div
                            style={{
                                padding: 40,
                                textAlign: "center"
                            }}
                        >
                            Loading...
                        </div>

                        :

                        filtered.length === 0 ?

                            <div
                                style={{
                                    padding: 40,
                                    textAlign: "center"
                                }}
                            >
                                No Client Claims Found
                            </div>

                            :

                            filtered.map((claim) => (

                                <TableRow

                                    key={claim.id}

                                    claim={claim}

                                    onDelete={handleDelete}

                                    onView={handleView}

                                    onEdit={handleEdit}

                                />

                            ))

                }

            </div>

            {
                showViewModal &&

                <ViewClientClaimModal

                    id={selectedClaimId}

                    close={() => {
                        setShowViewModal(false)
                    }}

                />

            }
            {
                showEditModal &&

                <EditClientClaimModal

                    id={selectedClaimId}

                    close={() => {
                        setShowEditModal(false)
                    }}

                    refresh={fetchClientClaims}

                />

            }
            {
                showAddModal &&

                <AddClientClaimModal

                    close={() => {
                        setShowAddModal(false)
                    }}

                    refresh={fetchClientClaims}

                />

            }

        </div>

    );

};

export default ClientClaim;